import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/LoadingScreen';
import supabase from '../utils/supabaseClient';

// Define the development stages for filtering
const DEVELOPMENT_STAGES = [
  'All Stages',
  'Discovery',
  'Preclinical',
  'Phase I',
  'Phase II',
  'Phase III',
  'FDA Review',
  'Approved'
];

const ProductsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState('All Stages');
  const [sortBy, setSortBy] = useState('newest');
  
  // Fetch products from Supabase
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Get products for the current user
        const { data, error: supabaseError } = await supabase
          .from('products')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        if (supabaseError) throw supabaseError;
        
        setProducts(data || []);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProducts();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);
  
  // Filter products based on search term and selected stage
  const filteredProducts = products.filter(product => {
    const matchesSearch = 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (product.concept_description && product.concept_description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.indications_of_use && product.indications_of_use.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStage = selectedStage === 'All Stages' || product.development_stage === selectedStage;
    
    return matchesSearch && matchesStage;
  });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      default:
        return 0;
    }
  });

  if (authLoading || loading) {
    return <LoadingScreen message="Loading products..." />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Your Products</h1>
        <Link to="/products/create" className="btn-primary">
          Create New Product
        </Link>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Search filter */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                id="search"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Stage filter */}
            <div>
              <label htmlFor="stage" className="block text-sm font-medium text-gray-700 mb-1">Development Stage</label>
              <select
                id="stage"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
              >
                {DEVELOPMENT_STAGES.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>

            {/* Sort filter */}
            <div>
              <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                id="sort"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-4">You haven't created any products yet.</p>
            <Link to="/products/create" className="btn-primary-outline">
              Get Started
            </Link>
          </div>
        </div>
      ) : (
        <>
          <p className="text-gray-600 mb-4">
            Found {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            {selectedStage !== 'All Stages' ? ` in "${selectedStage}" stage` : ''}
            {searchTerm ? ` matching "${searchTerm}"` : ''}
          </p>

          {sortedProducts.length === 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="p-8 text-center">
                <p className="text-gray-500 mb-4">No products match your current filters.</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedStage('All Stages');
                  }}
                  className="btn-secondary"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {sortedProducts.map((product) => (
                  <li key={product.id}>
                    <Link to={`/products/${product.id}`} className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <p className="text-lg font-medium text-primary-600 truncate">
                              {product.name}
                            </p>
                            <div className="ml-2 flex-shrink-0 flex">
                              <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                {product.development_stage}
                              </p>
                              <p className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                {product.product_category}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <p>
                              Updated {new Date(product.updated_at).toLocaleDateString()}
                            </p>
                            <svg className="ml-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {product.concept_description || 'No description available'}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductsPage; 